import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Settings, Play, Pause, RotateCcw, SkipForward, X, Mic, Upload, Heart, Trash2 } from 'lucide-react';

const BINGO_LETTERS = ['B', 'I', 'N', 'G', 'O'];
const LETTER_COLORS = {
  B: '#FF0088', I: '#00FFFF', N: '#00FF00', G: '#CC00FF', O: '#FFFF00'
};
const LETTER_RANGES = { B: [1,15], I: [16,30], N: [31,45], G: [46,60], O: [61,75] };

const THEMES = {
  neon: { B: '#FF0088', I: '#00FFFF', N: '#00FF00', G: '#CC00FF', O: '#FFFF00', start: '#00FF00', reset: '#FF0088', next: '#00FFFF', settings: '#FFFF00', divTop: '#00FFFF', divBot: '#FF0088', ballBg: '#333', calledBg: null },
  classic: { B: '#0066FF', I: '#FF0000', N: '#FFFFFF', G: '#00AA00', O: '#FF8800', start: '#00AA00', reset: '#CC0000', next: '#0066FF', settings: '#FF8800', divTop: '#0066FF', divBot: '#CC0000', ballBg: '#555', calledBg: null },
  pastel: { B: '#88AAFF', I: '#FF8888', N: '#88FF88', G: '#DD88FF', O: '#FFDD88', start: '#88FF88', reset: '#FF8888', next: '#88AAFF', settings: '#FFDD88', divTop: '#88AAFF', divBot: '#FF8888', ballBg: '#444', calledBg: null },
};

function getLetter(num) {
  if (num <= 15) return 'B';
  if (num <= 30) return 'I';
  if (num <= 45) return 'N';
  if (num <= 60) return 'G';
  return 'O';
}

export default function BingoBallDraw({ onAutoMarkup, onReset, className }) {
  const [calledBalls, setCalledBalls] = useState([]);
  const [currentBall, setCurrentBall] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(5);
  const [theme, setTheme] = useState('neon');
  const [ballStyle, setBallStyle] = useState('solid');
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('appearance');
  const [topColor, setTopColor] = useState('#0a0a0a');
  const [bottomColor, setBottomColor] = useState('#000000');
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [favoriteVoices, setFavoriteVoices] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shuffleDisplay, setShuffleDisplay] = useState(null); // {letter, num} shown during shuffle
  const timerRef = useRef(null);
  const shuffleTimerRef = useRef(null);

  const colors = THEMES[theme];

  // Load browser voices - English only, Natural voices preferred
  const selectedVoiceRef = useRef(selectedVoice);
  selectedVoiceRef.current = selectedVoice;

  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis?.getVoices() || [];
      const enOnly = voices.filter(v => v.lang.startsWith('en'));
      setAvailableVoices(enOnly.length > 0 ? enOnly : voices.filter(v => v.lang.startsWith('en')));
      // Only set default if no voice has been selected yet
      if (!selectedVoiceRef.current && enOnly.length > 0) {
        const natural = enOnly.find(v => v.name.toLowerCase().includes('natural')) || enOnly[0];
        setSelectedVoice(natural.name);
      }
    };
    loadVoices();
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices);
  }, []);

  const remaining = useMemo(() => {
    const all = Array.from({ length: 75 }, (_, i) => i + 1);
    return all.filter(n => !calledBalls.includes(n));
  }, [calledBalls]);

  const speak = useCallback((text) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = availableVoices.find(v => v.name === selectedVoice);
    if (voice) u.voice = voice;
    u.rate = 1;
    u.pitch = 1;
    window.speechSynthesis.speak(u);
  }, [voiceEnabled, selectedVoice, availableVoices]);

  const drawBall = useCallback(() => {
    if (remaining.length === 0) {
      setIsPlaying(false);
      if (timerRef.current) clearInterval(timerRef.current);
      return null;
    }
    const idx = Math.floor(Math.random() * remaining.length);
    const ball = remaining[idx];
    const letter = getLetter(ball);
    setCurrentBall(ball);
    setCalledBalls(prev => [...prev, ball]);
    speak(`${letter} ${ball}`);
    if (onAutoMarkup) onAutoMarkup(ball);
    return ball;
  }, [remaining, speak, onAutoMarkup]);

  const handleStart = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    setIsPlaying(true);
    drawBall();
    timerRef.current = setInterval(() => {
      drawBall();
    }, speed * 1000);
  }, [isPlaying, drawBall, speed]);

  const handleNext = useCallback(() => {
    drawBall();
  }, [drawBall]);

  const handleReset = useCallback(() => {
    setIsPlaying(false);
    if (timerRef.current) clearInterval(timerRef.current);
    window.speechSynthesis?.cancel();

    // Clear state immediately
    setCalledBalls([]);
    setCurrentBall(null);
    if (onReset) onReset();

    // Shuffle animation on the main ball
    setIsShuffling(true);

    // Play shuffle sound via Web Audio API
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const duration = 1.2;
      const buf = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        const t = i / ctx.sampleRate;
        const burst = Math.sin(t * 60) > 0 ? 1 : 0.3;
        const fade = 1 - (t / duration) * 0.7;
        data[i] = (Math.random() * 2 - 1) * 0.15 * burst * fade;
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 800;
      filter.Q.value = 1.5;
      src.connect(filter);
      filter.connect(ctx.destination);
      src.start();
      src.onended = () => ctx.close();
    } catch (e) {}

    // Rapidly cycle random numbers on the main ball display
    let count = 0;
    const maxCycles = 18;
    if (shuffleTimerRef.current) clearInterval(shuffleTimerRef.current);
    shuffleTimerRef.current = setInterval(() => {
      const rn = Math.floor(Math.random() * 75) + 1;
      setShuffleDisplay({ letter: getLetter(rn), num: rn });
      count++;
      if (count >= maxCycles) {
        clearInterval(shuffleTimerRef.current);
        shuffleTimerRef.current = null;
        setIsShuffling(false);
        setShuffleDisplay(null);
      }
    }, 65);
  }, []);

  // Update timer when speed changes during play
  useEffect(() => {
    if (isPlaying) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => drawBall(), speed * 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [speed, isPlaying, drawBall]);

  const calledSet = useMemo(() => new Set(calledBalls), [calledBalls]);

  const currentLetter = currentBall ? getLetter(currentBall) : null;
  const currentColor = currentLetter ? colors[currentLetter] : '#555';

  return (
    <div className={`flex flex-col h-full select-none ${className || ''}`} style={{ background: `linear-gradient(to bottom, ${topColor}, ${bottomColor})` }}>
      <style>{`
        @keyframes ballShuffle {
          0% { transform: rotate(-8deg) scale(1.05); }
          100% { transform: rotate(8deg) scale(0.95); }
        }
      `}</style>
      {/* Controls row - single compact line */}
      <div className="px-1.5 py-1 flex items-center gap-1.5 flex-shrink-0" style={{ background: topColor }}>
        {/* Current Ball - larger */}
        {(() => {
          const showBall = isShuffling ? shuffleDisplay : (currentBall ? { letter: currentLetter, num: currentBall } : null);
          const ballColor = showBall ? colors[showBall.letter] : '#555';
          return (
            <div className="flex-shrink-0 w-12 h-12 rounded-full flex flex-col items-center justify-center"
              style={{
                background: showBall ? `radial-gradient(ellipse, ${ballColor}44, ${ballColor}22)` : '#444',
                border: `2px solid ${showBall ? ballColor : '#555'}`,
                boxShadow: showBall ? `0 0 15px ${ballColor}66` : 'none',
                animation: isShuffling ? 'ballShuffle 0.15s ease-in-out infinite alternate' : 'none',
              }}>
              {showBall ? (
                <>
                  <span className="text-[8px] font-black leading-none" style={{ color: ballColor }}>{showBall.letter}</span>
                  <span className="text-lg font-black text-white leading-none">{showBall.num}</span>
                </>
              ) : (
                <span className="text-[8px] font-bold text-gray-400 text-center leading-tight">GO</span>
              )}
            </div>
          );
        })()}

        {/* Ball count */}
        <span className="text-white font-black text-[9px] leading-tight flex-shrink-0">{calledBalls.length}/75</span>

        {/* Compact icon-only buttons */}
        <div className="flex gap-0.5 flex-shrink-0">
          <button data-testid="ball-start-btn" onClick={handleStart}
            className="w-7 h-7 rounded flex items-center justify-center text-black transition-all hover:brightness-110"
            style={{ background: colors.start }} title={isPlaying ? 'Pause' : 'Start'}>
            {isPlaying ? <Pause className="w-3.5 h-3.5" fill="black" /> : <Play className="w-3.5 h-3.5" fill="black" />}
          </button>
          <button data-testid="ball-reset-btn" onClick={handleReset}
            className="w-7 h-7 rounded flex items-center justify-center text-black transition-all hover:brightness-110"
            style={{ background: colors.reset }} title="Reset">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button data-testid="ball-next-btn" onClick={handleNext}
            className="w-7 h-7 rounded flex items-center justify-center text-black transition-all hover:brightness-110"
            style={{ background: colors.next }} title="Next">
            <SkipForward className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* History - scrollable inline, right next to buttons */}
        <div className="flex-1 overflow-x-auto flex items-center gap-0.5 min-w-0" style={{ scrollbarWidth: 'none' }}>
          {calledBalls.length > 0 ? (
            [...calledBalls].reverse().map((b, i) => (
              <span key={i} className="text-[8px] font-bold px-1 py-0.5 rounded whitespace-nowrap flex-shrink-0"
                style={{ background: THEMES[theme][getLetter(b)] + '33', color: THEMES[theme][getLetter(b)] }}>
                {getLetter(b)}-{b}
              </span>
            ))
          ) : (
            <span className="text-[8px] text-gray-500">---</span>
          )}
        </div>

        {/* Settings */}
        <button data-testid="ball-settings-btn" onClick={() => setShowSettings(true)}
          className="w-6 h-6 rounded flex items-center justify-center text-black transition-all hover:brightness-110 flex-shrink-0"
          style={{ background: colors.next }}>
          <Settings className="w-3 h-3" />
        </button>
      </div>

      {/* Thin divider */}
      <div className="h-px flex-shrink-0" style={{ background: `linear-gradient(to right, ${colors.divTop}, ${colors.divBot})` }} />

      {/* Number Grid - dynamically sized to fill available space */}
      <div className="flex-1 flex flex-col overflow-hidden px-2 py-0.5" style={{ background: bottomColor, minHeight: 0 }}>
        {BINGO_LETTERS.map((letter) => {
          const [start, end] = LETTER_RANGES[letter];
          const letterColor = colors[letter];
          return (
            <div key={letter} className="flex-1 flex items-center gap-0.5 min-h-0">
              {/* Letter */}
              <div className="w-4 flex-shrink-0 text-center font-black"
                style={{ color: letterColor, textShadow: `0 0 8px ${letterColor}88`, fontSize: 'clamp(8px, 1.2vh, 14px)' }}>
                {letter}
              </div>
              {/* Number balls */}
              <div className="flex-1 flex gap-px h-full items-center">
                {Array.from({ length: end - start + 1 }, (_, i) => {
                  const num = start + i;
                  const isCalled = calledSet.has(num);
                  const isCurrentBall = currentBall === num;
                  const isOutline = ballStyle === 'outline';
                  return (
                    <div key={num}
                      className="flex-1 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                      style={{
                        height: '90%',
                        maxHeight: '40px',
                        background: isCalled
                          ? (isOutline ? `radial-gradient(ellipse, ${letterColor}22, transparent)` : letterColor)
                          : colors.ballBg || '#333',
                        border: isCalled
                          ? `2px solid ${letterColor}`
                          : '1px solid #444',
                        boxShadow: isCurrentBall
                          ? `0 0 12px ${letterColor}, 0 0 20px ${letterColor}44`
                          : isCalled
                            ? `0 0 6px ${letterColor}55`
                            : 'none',
                        transform: isCurrentBall ? 'scale(1.1)' : 'scale(1)',
                        minWidth: 0,
                      }}
                      onClick={() => {
                        if (!isCalled) {
                          setCurrentBall(num);
                          setCalledBalls(prev => [...prev, num]);
                          speak(`${letter} ${num}`);
                          if (onAutoMarkup) onAutoMarkup(num);
                        }
                      }}
                    >
                      <span className="font-black leading-none"
                        style={{
                          color: isCalled ? (isOutline ? '#fff' : '#000') : '#888',
                          fontSize: 'clamp(7px, 1.1vw, 14px)',
                          textShadow: isCalled
                            ? (isOutline ? `0 0 6px ${letterColor}, 0 0 10px ${letterColor}88` : `0 1px 2px rgba(0,0,0,0.5)`)
                            : 'none',
                        }}>
                        {num}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowSettings(false)}>
          <div className="w-[420px] max-h-[80vh] overflow-y-auto rounded-xl p-5"
            style={{ background: '#0d1929', border: `2px solid ${colors.divTop}`, boxShadow: `0 0 30px ${colors.divTop}33` }}
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-black italic" style={{ color: colors.divTop }}>Settings</h2>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex rounded-lg overflow-hidden mb-5" style={{ border: `1px solid ${colors.divTop}44`, background: '#1a2a40' }}>
              {['appearance', 'background', 'voice', 'custom'].map(tab => (
                <button key={tab} onClick={() => setSettingsTab(tab)}
                  className={`flex-1 py-2 text-sm font-bold capitalize transition-colors ${
                    settingsTab === tab ? 'bg-white text-black' : 'text-gray-400 hover:text-white'
                  }`}>{tab === 'appearance' ? 'Appearance' : tab === 'background' ? 'Background' : tab === 'voice' ? 'Voice' : 'Custom'}
                </button>
              ))}
            </div>

            {/* Appearance Tab */}
            {settingsTab === 'appearance' && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-white font-black text-sm mb-3">AUTO-PLAY TIMER</h3>
                  <div className="flex items-center gap-3">
                    <input type="range" min={1} max={15} step={0.5} value={speed}
                      onChange={(e) => setSpeed(Number(e.target.value))}
                      className="flex-1 h-2 cursor-pointer accent-white" />
                    <span className="text-white font-black text-sm w-10 text-right">{speed}s</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-sm mb-3">COLOR THEME</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.keys(THEMES).map(t => (
                      <button key={t} onClick={() => setTheme(t)}
                        className={`py-3 rounded-lg font-bold text-sm capitalize transition-all ${
                          theme === t ? 'text-black' : 'text-gray-400 hover:text-white'
                        }`}
                        style={{
                          background: theme === t ? THEMES[t].next : '#1a2a40',
                          border: `1px solid ${theme === t ? THEMES[t].next : '#333'}`,
                        }}>{t}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-sm mb-3">BALL STYLE</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['solid', 'outline'].map(s => (
                      <button key={s} onClick={() => setBallStyle(s)}
                        className={`py-3 rounded-lg font-bold text-sm capitalize transition-all ${
                          ballStyle === s ? 'text-black' : 'text-gray-400 hover:text-white'
                        }`}
                        style={{
                          background: ballStyle === s ? colors.reset : '#1a2a40',
                          border: `1px solid ${ballStyle === s ? colors.reset : '#333'}`,
                        }}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Background Tab */}
            {settingsTab === 'background' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-black text-xs mb-2">TOP SECTION COLOR</h3>
                  <div className="flex items-center gap-2">
                    <input type="color" value={topColor} onChange={(e) => setTopColor(e.target.value)}
                      className="w-10 h-10 cursor-pointer rounded border-2 border-gray-600" />
                    <span className="text-gray-400 text-sm font-mono">{topColor}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-white font-black text-xs mb-2">BOTTOM SECTION COLOR</h3>
                  <div className="flex items-center gap-2">
                    <input type="color" value={bottomColor} onChange={(e) => setBottomColor(e.target.value)}
                      className="w-10 h-10 cursor-pointer rounded border-2 border-gray-600" />
                    <span className="text-gray-400 text-sm font-mono">{bottomColor}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Voice Tab */}
            {settingsTab === 'voice' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-black text-xs mb-2">VOICE MODE</h3>
                  <select value={voiceEnabled ? 'tts' : 'off'}
                    onChange={(e) => setVoiceEnabled(e.target.value === 'tts')}
                    className="w-full p-2.5 rounded-lg text-sm text-white"
                    style={{ background: '#1a2a40', border: `1px solid ${colors.divTop}44` }}>
                    <option value="tts">Browser TTS</option>
                    <option value="off">Off</option>
                  </select>
                </div>
                {voiceEnabled && (
                  <>
                    <div>
                      <h3 className="text-white font-black text-xs mb-2">SELECT VOICE (ENGLISH)</h3>
                      <select value={selectedVoice}
                        onChange={(e) => setSelectedVoice(e.target.value)}
                        className="w-full p-2.5 rounded-lg text-sm text-white"
                        style={{ background: '#1a2a40', border: `1px solid ${colors.divTop}44` }}>
                        {availableVoices.map(v => (
                          <option key={v.name} value={v.name}>{v.name} - {v.lang}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-white font-black text-xs">FAVORITES</h3>
                        <button onClick={() => {
                          if (selectedVoice && !favoriteVoices.includes(selectedVoice)) {
                            setFavoriteVoices(prev => [...prev, selectedVoice]);
                          }
                        }}
                          className="px-3 py-1 bg-green-500 text-black text-xs font-bold rounded flex items-center gap-1">
                          <Heart className="w-3 h-3" /> Save
                        </button>
                      </div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {favoriteVoices.map(v => (
                          <div key={v} className="flex items-center gap-2 p-2 rounded" style={{ background: '#1a2a40' }}>
                            <span className="flex-1 text-xs text-gray-300 truncate">{v}</span>
                            <button onClick={() => setSelectedVoice(v)}
                              className="px-2 py-0.5 text-[10px] font-bold rounded" style={{ background: colors.divTop, color: 'black' }}>Load</button>
                            <button onClick={() => setFavoriteVoices(prev => prev.filter(f => f !== v))}
                              className="px-2 py-0.5 text-[10px] font-bold rounded bg-red-600 text-white">X</button>
                          </div>
                        ))}
                        {favoriteVoices.length === 0 && <p className="text-gray-500 text-xs">No favorites saved</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Custom Tab */}
            {settingsTab === 'custom' && (
              <div className="space-y-4">
                <p className="text-gray-400 text-sm">Record or upload for all 75 numbers at once.</p>
                <div className="grid grid-cols-2 gap-3">
                  <button className="py-3 rounded-lg font-bold text-sm text-white flex items-center justify-center gap-2"
                    style={{ background: '#4477FF' }}>
                    <Mic className="w-4 h-4" /> Record All 75
                  </button>
                  <button className="py-3 rounded-lg font-bold text-sm text-white flex items-center justify-center gap-2"
                    style={{ background: '#00AA44' }}>
                    <Upload className="w-4 h-4" /> Upload 75
                  </button>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                  <span className="text-gray-400 text-sm">Custom: 0/75</span>
                  <button className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
